import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { SectionHeader } from "@/components/Shared";
import { fmtDate } from "@/lib/format";
import DataTable from "@/components/DataTable";
import HealthBadge, { documentHealth } from "@/components/HealthBadge";
import DepartmentFilter, { useDepartmentFilter } from "@/components/DepartmentFilter";
import FirmFilter from "@/components/FirmFilter";
import { usePermissions } from "@/contexts/PermissionsContext";
import { Plus, X, Download, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";
import EmployeePicker from "@/components/EmployeePicker";
import VehiclePicker from "@/components/VehiclePicker";
import ConfirmDialog from "@/components/ConfirmDialog";
import { useBulkSelect, BulkCheckbox, BulkActionBar } from "@/components/BulkSelect";
import ModuleHint from "@/components/ModuleHint";

const EMPTY_FORM = {
  owner_type: "employee", owner_id: "", doc_type: "Führerschein",
  subject: "", description: "", expiry_date: "", file: null,
};

export default function Dokumente() {
  const { can } = usePermissions();
  const canCreate = can("dokumente", "create");
  const canDelete = can("dokumente", "delete");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [ownerType, setOwnerType] = useState("");
  const [deptFilter, setDeptFilter] = useDepartmentFilter("dokumente");
  const [showForm, setShowForm] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [filePreview, setFilePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sp, setSp] = useSearchParams();

  // Phase 5Z.25 — bulk-select + confirm dialog
  const bulk = useBulkSelect(docs);
  const [confirmState, setConfirmState] = useState(null); // {type:"single"|"bulk", id?, count?}
  const [confirmBusy, setConfirmBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = { q, owner_type: ownerType };
      if (deptFilter) params.department_id = deptFilter;
      const { data } = await api.get("/documents", { params });
      setDocs(data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [q, ownerType, deptFilter]);
  useEffect(() => {
    api.get("/employees").then(({ data }) => setEmployees(data));
    api.get("/vehicles").then(({ data }) => setVehicles(data));
  }, []);
  useEffect(() => {
    if (sp.get("new") === "1") {
      setShowForm(true);
      sp.delete("new"); setSp(sp, { replace: true });
    }
  }, [sp, setSp]);

  function pickFile(file) {
    setForm({ ...form, file });
    if (filePreview) URL.revokeObjectURL(filePreview);
    if (file && file.type.startsWith("image/")) {
      setFilePreview(URL.createObjectURL(file));
    } else {
      setFilePreview(null);
    }
  }
  function closeForm() {
    setShowForm(false);
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFilePreview(null);
    setForm(EMPTY_FORM);
  }

  async function save() {
    if (!form.subject.trim()) { toast.error("Bezeichnung ist Pflichtfeld"); return; }
    if (form.owner_type !== "company" && !form.owner_id) { toast.error("Bitte Eigentümer wählen"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (k !== "file" && v !== null && v !== undefined) fd.append(k, v); });
      if (form.file) fd.append("file", form.file);
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Dokument hochgeladen");
      closeForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally { setUploading(false); }
  }
  async function remove(id) {
    setConfirmState({ type: "single", id });
  }
  async function confirmRemove() {
    if (!confirmState) return;
    setConfirmBusy(true);
    try {
      if (confirmState.type === "single") {
        await api.delete(`/documents/${confirmState.id}`);
        toast.success("Gelöscht");
      } else {
        const ids = bulk.selectedIds;
        const results = await Promise.allSettled(ids.map((id) => api.delete(`/documents/${id}`)));
        const ok = results.filter((r) => r.status === "fulfilled").length;
        const fail = results.length - ok;
        if (ok) toast.success(`${ok} Dokument${ok === 1 ? "" : "e"} gelöscht`);
        if (fail) toast.error(`${fail} fehlgeschlagen`);
        bulk.clear();
      }
      load();
    } catch { toast.error("Löschen fehlgeschlagen"); }
    finally { setConfirmBusy(false); setConfirmState(null); }
  }
  function ownerName(d) {
    if (d.owner_type === "employee") {
      const e = employees.find((x) => x.id === d.owner_id); return e ? `${e.first_name} ${e.last_name}` : "—";
    }
    if (d.owner_type === "vehicle") {
      const v = vehicles.find((x) => x.id === d.owner_id); return v ? v.license_plate : "—";
    }
    return "Firma";
  }
  async function download(doc) {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = doc.file_name || doc.subject;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download fehlgeschlagen"); }
  }
  async function preview(doc) {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch { toast.error("Vorschau fehlgeschlagen"); }
  }

  return (
    <div className="p-6 md:p-8" data-testid="dokumente-page">
      <SectionHeader
        title="Dokumente"
        subtitle="Mitarbeiter- und Fahrzeugdokumente"
        actions={
          canCreate ? (
            <button data-testid="add-doc-btn" onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md">
              <Plus className="w-4 h-4" /> Dokument
            </button>
          ) : null
        }
      />

      <ModuleHint moduleKey="dokumente" />

      <BulkActionBar
        count={bulk.selectedCount}
        onClear={bulk.clear}
        onDelete={() => canDelete && setConfirmState({ type: "bulk", count: bulk.selectedCount })}
        deleteLabel={`${bulk.selectedCount} löschen`}
        testid="dokumente-bulk-bar"
      />

      <DataTable
        rows={docs}
        loading={loading}
        searchTerm={q}
        onSearchChange={setQ}
        searchPlaceholder="Dokumenttyp, Bezeichnung, Beschreibung..."
        testid="dokumente-table"
        emptyTitle="Keine Dokumente gefunden"
        emptyHint={q || ownerType ? "Versuchen Sie andere Filter." : "Laden Sie das erste Dokument hoch."}
        filters={
          <div className="flex items-center gap-2 flex-wrap">
            <FirmFilter compact />
            <DepartmentFilter value={deptFilter} onChange={setDeptFilter} module="dokumente" testid="dokumente-dept-filter"/>
            <select value={ownerType} onChange={(e) => setOwnerType(e.target.value)} data-testid="dokumente-owner-filter"
              className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
              <option value="">Alle Typen</option>
              <option value="employee">Mitarbeiter</option>
              <option value="vehicle">Fahrzeug</option>
              <option value="company">Firma</option>
            </select>
          </div>
        }
        columns={[
          {
            key: "_select",
            label: <BulkCheckbox checked={bulk.allSelected} indeterminate={bulk.someSelected} onChange={bulk.toggleAll} disabled={!canDelete} testid="dokumente-select-all" />,
            headerClassName: "w-8",
            className: "w-8",
            render: (d) => (
              <BulkCheckbox checked={bulk.isSelected(d.id)} onChange={() => bulk.toggle(d.id)} disabled={!canDelete} testid={`dokumente-select-${d.id}`} />
            ),
          },
          { key: "doc_type", label: "Typ", sortable: true, render: (d) => <span className="text-zinc-300">{d.doc_type}</span> },
          { key: "subject", label: "Bezeichnung", sortable: true, render: (d) => <span className="text-zinc-100 font-medium">{d.subject}</span> },
          { key: "_owner", label: "Eigentümer", render: (d) => <span className="text-zinc-300">{ownerName(d)}</span> },
          { key: "expiry_date", label: "Ablauf", sortable: true, render: (d) => <span className="font-mono text-xs text-zinc-400">{fmtDate(d.expiry_date)}</span> },
          { key: "doc_status", label: "Status", sortable: true, render: (d) => {
              const h = documentHealth(d);
              return <HealthBadge level={h.level} reasons={h.reasons} size="xs" variant="badge" testid={`doc-health-${d.id}`} />;
          } },
          {
            key: "_actions", label: "",
            headerClassName: "text-right", className: "text-right",
            render: (d) => (
              <>
                {d.file_name && (
                  <>
                    <button onClick={() => preview(d)} title="Vorschau" className="p-1.5 text-zinc-400 hover:text-blue-400" data-testid={`doc-preview-${d.id}`}><Eye className="w-3.5 h-3.5" /></button>
                    <button onClick={() => download(d)} title="Download" className="p-1.5 text-zinc-400 hover:text-blue-400 ml-1" data-testid={`doc-download-${d.id}`}><Download className="w-3.5 h-3.5" /></button>
                  </>
                )}
                <button onClick={() => remove(d.id)} title="Löschen" className={`p-1.5 ml-1 ${canDelete ? "text-zinc-400 hover:text-red-400" : "text-zinc-700 cursor-not-allowed"}`} data-testid={`doc-delete-${d.id}`} disabled={!canDelete}><Trash2 className="w-3.5 h-3.5" /></button>
              </>
            ),
          },
        ]}
      />

      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="font-display font-semibold text-zinc-50">Neues Dokument</h3>
              <button onClick={closeForm} className="text-zinc-400" data-testid="doc-form-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-4">
              <div><label className="text-[10px] uppercase text-zinc-500">Eigentümer-Typ</label>
                <select value={form.owner_type} onChange={(e) => setForm({ ...form, owner_type: e.target.value, owner_id: "" })} className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1">
                  <option value="employee">Mitarbeiter</option><option value="vehicle">Fahrzeug</option><option value="company">Firma</option>
                </select></div>
              <div><label className="text-[10px] uppercase text-zinc-500">Eigentümer</label>
                <div className="mt-1">
                  {form.owner_type === "employee" && (
                    <EmployeePicker value={form.owner_id} testid="doc-owner-emp-picker"
                      onChange={(v) => setForm({ ...form, owner_id: v })} />
                  )}
                  {form.owner_type === "vehicle" && (
                    <VehiclePicker value={form.owner_id} testid="doc-owner-veh-picker"
                      onChange={(v) => setForm({ ...form, owner_id: v })} />
                  )}
                  {form.owner_type === "company" && (
                    <div className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-500 italic">
                      Firmen-Dokument (kein Eigentümer)
                    </div>
                  )}
                </div></div>
              <div><label className="text-[10px] uppercase text-zinc-500">Typ</label>
                <select value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1">
                  {["Führerschein", "Arbeitsvertrag", "Zulassungsschein", "Versicherung", "§57a Gutachten", "Service", "Lieferschein", "Reisepass", "Personalausweis", "Versicherungspolizze", "§57a Pickerl", "Sonstiges"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select></div>
              <div><label className="text-[10px] uppercase text-zinc-500">Ablaufdatum</label>
                <input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1" /></div>
              <div className="col-span-2"><label className="text-[10px] uppercase text-zinc-500">Bezeichnung *</label>
                <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={`w-full px-3 py-2 bg-zinc-950 border rounded text-sm text-zinc-100 mt-1 ${!form.subject.trim() ? "border-red-500/40" : "border-zinc-800"}`} data-testid="doc-subject-input" /></div>
              <div className="col-span-2"><label className="text-[10px] uppercase text-zinc-500">Beschreibung</label>
                <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1" /></div>
              <div className="col-span-2"><label className="text-[10px] uppercase text-zinc-500">Datei</label>
                <input type="file" onChange={(e) => pickFile(e.target.files[0])} className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 mt-1 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-blue-600 file:text-white file:text-xs" data-testid="doc-file-input" />
                {form.file && (
                  <div className="mt-2 flex items-center gap-3 text-xs text-zinc-400">
                    {filePreview ? (
                      <img src={filePreview} alt="Preview" className="w-16 h-16 object-cover rounded border border-zinc-800" data-testid="doc-file-preview" />
                    ) : (
                      <div className="w-16 h-16 flex items-center justify-center bg-zinc-950 border border-zinc-800 rounded text-zinc-500 text-[10px] uppercase">{(form.file.name.split(".").pop() || "FILE").toUpperCase()}</div>
                    )}
                    <div>
                      <div className="text-zinc-200 truncate max-w-xs">{form.file.name}</div>
                      <div className="text-zinc-500 text-[11px]">{(form.file.size / 1024).toFixed(1)} KB</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
              <button onClick={closeForm} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
              <button onClick={save} disabled={uploading} data-testid="doc-save-btn" className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 disabled:text-zinc-500 text-white rounded font-medium">
                {uploading ? "Lade hoch…" : "Hochladen"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.type === "bulk" ? "Mehrere Dokumente löschen?" : "Dokument löschen?"}
        message={confirmState?.type === "bulk"
          ? `${confirmState?.count} Dokument${confirmState?.count === 1 ? "" : "e"} werden unwiderruflich gelöscht. Fortfahren?`
          : "Diese Aktion kann nicht rückgängig gemacht werden."}
        confirmLabel={confirmState?.type === "bulk" ? `Ja, ${confirmState?.count} löschen` : "Ja, löschen"}
        cancelLabel="Abbrechen"
        tone="danger"
        busy={confirmBusy}
        onConfirm={confirmRemove}
        onCancel={() => !confirmBusy && setConfirmState(null)}
        testid="dokumente-confirm-delete"
      />
    </div>
  );
}
